import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { useTranslation } from 'react-i18next';
import axios from '@/utils/axiosConfig';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageSquare, Send, Trash2, Users, Bell, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const Messages = () => {
  const { user, token } = useAuth();
  const { refreshUnreadCount } = useUnreadMessages();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [conversation, setConversation] = useState([]);
  const [newMessage, setNewMessage] = useState({ subject: '', content: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [selectedMessages, setSelectedMessages] = useState(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchMessages();
    fetchUsers();
    fetchUnreadCount();
    
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchMessages();
      if (selectedContact) {
        fetchConversation(selectedContact.id);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [selectedContact]);

  useEffect(() => {
    if (selectedContact) {
      fetchConversation(selectedContact.id);
    }
    // eslint-disable-next-line
  }, [selectedContact]);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setMessages(response.data);
      }
    } catch (error) {
      if (isMountedRef.current) {
        toast.error(t('error'));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/messaging/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      if (isMountedRef.current) {
        toast.error('Erreur lors du chargement des contacts');
      }
    }
  };

  const fetchConversation = async (userId) => {
    try {
      const response = await axios.get(`${API}/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setConversation(response.data);
        markAsRead(userId);
      }
    } catch (error) {
      console.error('Failed to fetch conversation:', error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) {
        setUnreadCount(response.data.unread_count);
      }
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const markAsRead = async (senderId) => {
    try {
      await axios.put(
        `${API}/messages/mark-read/${senderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchUnreadCount();
      // Also refresh the global unread count in the sidebar
      refreshUnreadCount();
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!selectedContact) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }

    try {
      await axios.post(
        `${API}/messages`,
        {
          receiver_id: selectedContact.id,
          subject: newMessage.subject,
          content: newMessage.content
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Message envoyé');
      setNewMessage({ subject: '', content: '' });
      setDialogOpen(false);
      fetchMessages();
      fetchConversation(selectedContact.id);
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Supprimer ce message ?')) return;

    try {
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Message supprimé');
      fetchMessages();
      if (selectedContact) {
        fetchConversation(selectedContact.id);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMessages.size === 0) {
      toast.error('Aucun message sélectionné');
      return;
    }

    if (!window.confirm(`Supprimer ${selectedMessages.size} message(s) sélectionné(s) ?`)) return;

    try {
      const messageIds = Array.from(selectedMessages);
      await axios.post(
        `${API}/messages/bulk-delete`,
        messageIds,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`${selectedMessages.size} message(s) supprimé(s)`);
      setSelectedMessages(new Set());
      setIsSelectionMode(false);
      fetchMessages();
      if (selectedContact) {
        fetchConversation(selectedContact.id);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleMessageSelection = (messageId) => {
    const newSelection = new Set(selectedMessages);
    if (newSelection.has(messageId)) {
      newSelection.delete(messageId);
    } else {
      newSelection.add(messageId);
    }
    setSelectedMessages(newSelection);
  };

  const selectAllMessages = () => {
    if (selectedMessages.size === conversation.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(conversation.map(msg => msg.id)));
    }
  };

  // Group messages by contact to create conversation threads
  const getConversationThreads = () => {
    const threadsMap = new Map();
    
    users.forEach(u => {
      threadsMap.set(u.id, {
        id: u.id,
        full_name: u.full_name,
        role: u.role,
        messages: [],
        lastMessageDate: null,
        unreadCount: 0
      });
    });
    
    messages.forEach(msg => {
      const contactId = msg.sender_id === user.id ? msg.receiver_id : msg.sender_id;
      
      if (threadsMap.has(contactId)) {
        const thread = threadsMap.get(contactId);
        thread.messages.push(msg);
        
        const msgDate = new Date(msg.created_at);
        if (!thread.lastMessageDate || msgDate > thread.lastMessageDate) {
          thread.lastMessageDate = msgDate;
        }
        
        if (!msg.read && msg.receiver_id === user.id) {
          thread.unreadCount++;
        }
      }
    });
    
    // Filter threads that have messages and sort by last message date
    return Array.from(threadsMap.values())
      .filter(thread => thread.messages.length > 0)
      .sort((a, b) => b.lastMessageDate - a.lastMessageDate);
  };

  const conversationThreads = getConversationThreads();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900">
              {t('messages')}
            </h1>
            {unreadCount > 0 && (
              <Badge className="bg-red-600 text-white px-3 py-1 text-sm">
                <Bell className="h-4 w-4 mr-1" />
                {unreadCount} nouveau(x)
              </Badge>
            )}
          </div>
          
          <div className="flex gap-3">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-gradient-to-r from-red-600 to-blue-600"
                >
                  <Send className="mr-2 h-5 w-5" />
                  Nouveau message
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Nouveau message</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSendMessage} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Destinataire</Label>
                    <Select onValueChange={(value) => {
                      const selected = users.find(u => u.id === value);
                      setSelectedContact(selected);
                    }}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un destinataire" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name} ({u.role === 'admin' ? 'Admin' : u.role === 'employee' ? 'Employé' : 'Client'})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sujet</Label>
                    <Input
                      value={newMessage.subject}
                      onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                      placeholder="Objet du message"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Message</Label>
                    <Textarea
                      value={newMessage.content}
                      onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                      placeholder="Votre message..."
                      rows={6}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-red-600 to-blue-600"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Envoyer
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversation Threads List */}
          <Card className="border-0 shadow-xl lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-xl flex items-center justify-between">
                <span>Conversations</span>
                <Badge variant="outline">{conversationThreads.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y max-h-[600px] overflow-y-auto">
                {conversationThreads.map((thread) => (
                  <div
                    key={thread.id}
                    className={`p-4 cursor-pointer transition-colors ${
                      selectedContact?.id === thread.id
                        ? 'bg-blue-50 border-l-4 border-blue-600'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      setSelectedContact(thread);
                      fetchConversation(thread.id);
                      setIsSelectionMode(false);
                      setSelectedMessages(new Set());
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">{thread.full_name}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {thread.role === 'admin' ? 'Admin' : thread.role === 'employee' ? 'Employé' : 'Client'}
                        </Badge>
                        <p className="text-xs text-gray-500 mt-1">
                          {thread.messages.length} message(s)
                        </p>
                        {thread.lastMessageDate && (
                          <p className="text-xs text-gray-400 mt-1">
                            {format(thread.lastMessageDate, 'dd/MM/yyyy HH:mm')}
                          </p>
                        )}
                      </div>
                      {thread.unreadCount > 0 && (
                        <Badge className="bg-red-600 text-white text-xs">
                          {thread.unreadCount}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
                {conversationThreads.length === 0 && (
                  <div className="p-12 text-center text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Aucune conversation</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Conversation Thread Messages */}
          <Card className="border-0 shadow-xl lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">
                  {selectedContact ? (
                    <div>
                      <span>Conversation avec {selectedContact.full_name}</span>
                      <p className="text-sm font-normal text-gray-500 mt-1">
                        {conversation.length} message(s)
                      </p>
                    </div>
                  ) : (
                    'Sélectionnez une conversation'
                  )}
                </CardTitle>
                
                {selectedContact && conversation.length > 0 && (user?.role === 'admin' || user?.role === 'employee') && (
                  <div className="flex gap-2">
                    <Button
                      variant={isSelectionMode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => {
                        setIsSelectionMode(!isSelectionMode);
                        setSelectedMessages(new Set());
                      }}
                    >
                      <CheckSquare className="h-4 w-4 mr-2" />
                      Sélectionner
                    </Button>
                    
                    {isSelectionMode && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={selectAllMessages}
                        >
                          {selectedMessages.size === conversation.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDelete}
                          disabled={selectedMessages.size === 0}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer ({selectedMessages.size})
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {selectedContact ? (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {conversation.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).map((msg) => {
                    const isSentByMe = msg.sender_id === user.id;
                    const isSelected = selectedMessages.has(msg.id);
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}
                      >
                        {isSelectionMode && (
                          <div className="flex items-start mr-2 pt-2">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => toggleMessageSelection(msg.id)}
                            />
                          </div>
                        )}
                        
                        <div className="relative group max-w-[70%]">
                          <div
                            className={`p-4 rounded-lg ${
                              isSentByMe
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white'
                                : 'bg-gray-100 text-gray-900'
                            } ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <p className={`text-sm font-semibold ${isSentByMe ? 'text-blue-100' : 'text-gray-700'}`}>
                                {msg.subject}
                              </p>
                              {!isSelectionMode && (user?.role === 'admin' || isSentByMe) && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteMessage(msg.id)}
                                  className={`opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0 ml-2 ${
                                    isSentByMe ? 'text-white hover:bg-blue-800' : 'text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                            <p className="text-sm mb-2">{msg.content}</p>
                            <p className={`text-xs ${isSentByMe ? 'text-blue-200' : 'text-gray-500'}`}>
                              {format(new Date(msg.created_at), 'dd/MM/yyyy HH:mm')}
                            </p>
                            {!msg.read && !isSentByMe && (
                              <Badge className="mt-2 text-xs bg-red-600">Nouveau</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {conversation.length === 0 && (
                    <div className="text-center py-12 text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun message dans cette conversation</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Sélectionnez une conversation pour voir les messages</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default Messages;
